
import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Board presence user type (channel-based, ephemeral)
export type BoardPresenceUser = {
  user_id: string;
  user_name: string;
  status: "viewing" | "editing";
  card_id?: string;
  updated_at: string;
  joined_at: string;
  role: 'viewer' | 'editor' | 'admin';
};

interface BoardPresenceContextType {
  presenceUsers: BoardPresenceUser[];
  setUserStatus: (status: "viewing" | "editing", cardId?: string) => void;
  setupBoardPresence: (boardId: string) => Promise<() => void>;
  isConnected: boolean;
  getEditingUsersOnCard: (cardId: string) => BoardPresenceUser[];
  getUsersOnCard: (cardId: string) => BoardPresenceUser[];
  clearUserPresence: () => void;
}

const BoardPresenceContext = createContext<BoardPresenceContextType | undefined>(undefined);

export const useBoardPresence = () => {
  const context = useContext(BoardPresenceContext);
  if (!context) {
    throw new Error("useBoardPresence must be used within a BoardPresenceProvider");
  }
  return context;
};

interface BoardPresenceProviderProps {
  children: React.ReactNode;
  boardId?: string;
}

export const BoardPresenceProvider = ({ children, boardId }: BoardPresenceProviderProps) => {
  const { user } = useAuth();
  const [presenceUsers, setPresenceUsers] = useState<BoardPresenceUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  
  // Channel-based presence tracking
  const channelRef = useRef<any>(null);
  const boardIdRef = useRef<string | null>(null);
  const currentCardRef = useRef<string | null>(null);
  const currentStatusRef = useRef<"viewing" | "editing">("viewing");

  // Database operations for card-level presence (preserved functionality)
  const upsertCardPresence = useCallback(async (status: "viewing" | "editing", cardId?: string) => {
    if (!user || !boardIdRef.current || !cardId) return;

    try {
      const presenceData = {
        board_id: boardIdRef.current,
        card_id: cardId,
        user_id: user.id,
        user_name: user.email || user.user_metadata?.full_name || 'Anonymous',
        status,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('card_presence')
        .upsert([presenceData], { 
          onConflict: 'board_id,card_id,user_id',
          ignoreDuplicates: false 
        });

      if (error) {
        console.error('❌ Error upserting card presence:', error);
      }
    } catch (error) {
      console.error('❌ Exception in upsertCardPresence:', error);
    }
  }, [user]);

  // Fetch card-level presence from database
  const fetchCardPresence = useCallback(async (boardId: string) => {
    if (!boardId) return;

    try {
      const { data, error } = await supabase
        .from('card_presence')
        .select('*')
        .eq('board_id', boardId)
        .not('card_id', 'is', null); // Only card-level presence

      if (error) {
        console.error('❌ Error fetching card presence:', error);
        return;
      }

      // Convert to BoardPresenceUser format for card-level presence
      const cardPresenceUsers: BoardPresenceUser[] = (data || []).map(presence => ({
        user_id: presence.user_id,
        user_name: presence.user_name || 'Anonymous',
        status: presence.status as "viewing" | "editing",
        card_id: presence.card_id || undefined,
        updated_at: presence.updated_at,
        joined_at: presence.updated_at,
        role: 'viewer' as const
      }));

      // Merge channel presence with card presence
      setPresenceUsers(prevUsers => {
        const channelUsers = prevUsers.filter(user => !user.card_id);
        const uniqueCardUsers = cardPresenceUsers.filter(cardUser =>
          !channelUsers.some(channelUser => 
            channelUser.user_id === cardUser.user_id && channelUser.card_id === cardUser.card_id
          )
        );
        return [...channelUsers, ...uniqueCardUsers];
      });
    } catch (error) {
      console.error('❌ Exception in fetchCardPresence:', error);
    }
  }, []);

  const setUserStatus = useCallback((status: "viewing" | "editing", cardId?: string) => {
    currentStatusRef.current = status;
    currentCardRef.current = cardId || null;

    if (cardId) {
      // Card-level presence - use database
      upsertCardPresence(status, cardId);
    } else {
      // Board-level presence - use channel tracking
      if (channelRef.current) {
        channelRef.current.track({
          user_id: user?.id || '',
          user_name: user?.email || user?.user_metadata?.full_name || 'Anonymous',
          status,
          joined_at: new Date().toISOString(),
          role: 'viewer'
        });
      }
    }
  }, [upsertCardPresence, user]);

  const setupBoardPresence = useCallback(async (boardId: string) => {
    if (!user || !boardId) return () => {};

    boardIdRef.current = boardId;
    
    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Create new channel for board-level presence
    const channel = supabase.channel(`board_presence:${boardId}`, {
      config: { presence: { key: user.id }}
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const channelPresenceUsers: BoardPresenceUser[] = Object.values(state)
          .flat()
          .map((u: any) => ({
            user_id: u.user_id,
            user_name: u.user_name,
            status: u.status || "viewing",
            updated_at: new Date().toISOString(),
            joined_at: u.joined_at,
            role: u.role || "viewer",
          }))
          .filter(u => u.user_id !== user.id); // Exclude current user

        // Merge with existing card presence
        setPresenceUsers(prevUsers => {
          const cardUsers = prevUsers.filter(user => user.card_id);
          return [...channelPresenceUsers, ...cardUsers];
        });
      })
      .on("presence", { event: "join" }, () => {
        // Re-sync on join
        const state = channel.presenceState();
        const channelPresenceUsers: BoardPresenceUser[] = Object.values(state)
          .flat()
          .map((u: any) => ({
            user_id: u.user_id,
            user_name: u.user_name,
            status: u.status || "viewing",
            updated_at: new Date().toISOString(),
            joined_at: u.joined_at,
            role: u.role || "viewer",
          }))
          .filter(u => u.user_id !== user.id);

        setPresenceUsers(prevUsers => {
          const cardUsers = prevUsers.filter(user => user.card_id);
          return [...channelPresenceUsers, ...cardUsers];
        });
      })
      .on("presence", { event: "leave" }, () => {
        // Re-sync on leave
        const state = channel.presenceState();
        const channelPresenceUsers: BoardPresenceUser[] = Object.values(state)
          .flat()
          .map((u: any) => ({
            user_id: u.user_id,
            user_name: u.user_name,
            status: u.status || "viewing",
            updated_at: new Date().toISOString(),
            joined_at: u.joined_at,
            role: u.role || "viewer",
          }))
          .filter(u => u.user_id !== user.id);

        setPresenceUsers(prevUsers => {
          const cardUsers = prevUsers.filter(user => user.card_id);
          return [...channelPresenceUsers, ...cardUsers];
        });
      });

    // Subscribe to card presence changes in database
    const cardPresenceChannel = supabase
      .channel(`card_presence_${boardId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'card_presence',
          filter: `board_id=eq.${boardId}`
        },
        () => {
          fetchCardPresence(boardId);
        }
      )
      .subscribe();

    // Subscribe to channel presence
    channel.subscribe(async (status) => {
      setIsConnected(status === "SUBSCRIBED");
      if (status === "SUBSCRIBED") {
        // Track initial presence
        await channel.track({
          user_id: user.id,
          user_name: user.email || user.user_metadata?.full_name || 'Anonymous',
          status: currentStatusRef.current,
          joined_at: new Date().toISOString(),
          role: 'viewer'
        });

        // Fetch initial card presence
        await fetchCardPresence(boardId);
      }
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      supabase.removeChannel(cardPresenceChannel);
      setIsConnected(false);
    };
  }, [user, fetchCardPresence]);

  const getEditingUsersOnCard = useCallback((cardId: string): BoardPresenceUser[] => {
    return presenceUsers.filter(user => 
      user.card_id === cardId && 
      user.status === "editing" &&
      user.user_id !== user?.user_id
    );
  }, [presenceUsers, user]);

  const getUsersOnCard = useCallback((cardId: string): BoardPresenceUser[] => {
    return presenceUsers.filter(presenceUser => 
      presenceUser.card_id === cardId &&
      presenceUser.user_id !== user?.id
    );
  }, [presenceUsers, user]);

  const clearUserPresence = useCallback(() => {
    currentCardRef.current = null;
    currentStatusRef.current = "viewing";
  }, []);

  // Auto-setup board presence when boardId prop changes
  useEffect(() => {
    if (boardId) {
      setupBoardPresence(boardId);
    }
  }, [boardId, setupBoardPresence]);

  const value = {
    presenceUsers,
    setUserStatus,
    setupBoardPresence,
    isConnected,
    getEditingUsersOnCard,
    getUsersOnCard,
    clearUserPresence,
  };

  return (
    <BoardPresenceContext.Provider value={value}>
      {children}
    </BoardPresenceContext.Provider>
  );
};
